// ─── Historique des changements (Core) ────────────────────────────
// Journal chronologique de ce qui modifie l'organisation : événements, séries
// récurrentes, emploi du temps, garde, ET opérations financières — porté de
// MoPCal (`services/historique.js`), élargi.
//
// Pourquoi élargi : deux ans d'import bancaire ont disparu sans qu'aucune trace
// permette de dire QUAND ni COMMENT. L'historique ne couvrait ni les dépenses ni
// les suppressions en cascade ; il les couvre désormais.
//
// Stockage : SOUS-COLLECTION calendriers/{calId}/historique/{auto} — cloisonnée
// par calendrier et triable par date SANS index composite.
//
// La journalisation est BEST-EFFORT : elle ne doit JAMAIS bloquer ni faire
// échouer l'action de l'utilisateur. Toute erreur est avalée, et l'appel se fait
// en « fire-and-forget » (sans await) côté services.
import { collection, addDoc, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

//   type   : 'evenement' | 'recurrence' | 'edt' | 'garde' | 'operation'
//            | 'budget' | 'calendrier'
//   action : 'creation' | 'modification' | 'suppression'
// `cible` : un id de calendrier (string, forme historique) OU
// { calId } / { espaceId }. Les opérations financières de la suite se
// rattachent à un ESPACE — sans cette souplesse, une opération sans calendrier
// ne laissait aucune trace.
export async function journaliser(cible, entree) {
  const calId = typeof cible === 'string' ? cible : cible?.calId;
  const espaceId = typeof cible === 'string' ? null : cible?.espaceId;
  if ((!calId && !espaceId) || !entree) return;
  const chemin = calId ? ['calendriers', calId, 'historique'] : ['espaces', espaceId, 'historique'];
  try {
    await addDoc(collection(db, ...chemin), {
      type:          entree.type   || 'evenement',
      action:        entree.action || 'modification',
      titre:         entree.titre || '',
      dateConcernee: entree.dateConcernee || null,
      detail:        entree.detail || null,
      montant:       Number.isFinite(entree.montant) ? entree.montant : null,
      par:           entree.par || null,
      parNom:        entree.parNom || '',
      le:            new Date().toISOString(),
    });
  } catch { /* best-effort : on n'interrompt jamais l'action utilisateur */ }
}

// `onErreur` remonté — même correction que sur les contacts/le journal/la
// messagerie : un refus de règle donnait « aucun changement » en silence,
// indiscernable d'un calendrier qui n'a réellement aucun historique.
export function ecouterHistorique(cible, callback, max = 300, onErreur) {
  const calId = typeof cible === 'string' ? cible : cible?.calId;
  const espaceId = typeof cible === 'string' ? null : cible?.espaceId;
  if (!calId && !espaceId) { callback([]); return () => {}; }
  const chemin = calId ? ['calendriers', calId, 'historique'] : ['espaces', espaceId, 'historique'];
  return onSnapshot(
    query(collection(db, ...chemin), orderBy('le', 'desc'), limit(max)),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (e) => { callback([]); onErreur?.(e?.message || String(e)); },
  );
}

export default journaliser;
